import type {
  UserInfo,
  AdminCustomer,
  EmulationTarget,
  ConsentInfo,
  DashboardSummary,
  CallVolume,
  QualityMetrics,
  BadCallStats,
  TrendData,
  MyDashboardResponse,
  MySummary,
  MyTrends,
  MyTopContactsResponse,
  MyRecentCallsResponse,
  CallListResponse,
  CallDetail,
  CallFilters,
  CallLegsResponse,
  RecordingListResponse,
  RecordingDownloadResponse,
  BranchQualityResponse,
  ReportCategory,
  SupportReportSummary,
  SupportDestination,
} from '../types';
import type { CompanyDatasetResponse, CompanyHeatmapResponse } from '../types/company';
import type {
  QueuesDatasetResponse,
  TrendsResponse,
  ServiceLevelResponse,
  AADetailResponse,
  AgentDetailResponse,
} from '../types/queues';
import type { BranchAnalyticsResponse, BranchMetaResponse } from '../types/branch';

const API_BASE = '/api/v1';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface BranchScope {
  branchId?: number | null;
  unmapped?: boolean;
}

function branchScopeQs(scope?: BranchScope): string {
  if (!scope) return '';
  if (scope.unmapped) return '&unmapped=true';
  if (scope.branchId != null) return `&branch_id=${scope.branchId}`;
  return '';
}

class ApiService {
  private token: string | null = null;
  private onUnauthorized: (() => void | Promise<void>) | null = null;
  private isRetrying = false;
  private emulationContext: { customerAccountId: string; userEmail: string } | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  setOnUnauthorized(callback: (() => void | Promise<void>) | null) {
    this.onUnauthorized = callback;
  }

  setEmulationContext(context: { customerAccountId: string; userEmail: string } | null) {
    this.emulationContext = context;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOnUnauthorized = true
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.emulationContext) {
      headers['X-Emulate-Customer-Account-Id'] = this.emulationContext.customerAccountId;
      headers['X-Emulate-User-Email'] = this.emulationContext.userEmail;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - trigger token refresh
      if (response.status === 401 && retryOnUnauthorized && this.onUnauthorized && !this.isRetrying) {
        this.isRetrying = true;
        try {
          // Wait for the refresh itself (the provider returns its promise),
          // then retry once. A fixed sleep here raced a slow refresh.
          await this.onUnauthorized();
        } catch {
          // The provider swallows refresh errors; be defensive anyway.
        } finally {
          this.isRetrying = false;
        }

        // Retry the request (without retrying again on failure)
        return this.request<T>(endpoint, options, false);
      }

      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      const detail =
        typeof error?.detail === 'string' && error.detail.trim().length > 0
          ? error.detail
          : 'Request failed';

      if (response.status === 401) {
        const normalizedDetail = detail.toLowerCase();
        if (
          normalizedDetail.includes('token has expired') ||
          normalizedDetail.includes('invalid token') ||
          normalizedDetail.includes('token validation failed') ||
          normalizedDetail.includes('invalid authorization header')
        ) {
          throw new ApiError('Token expired. Please refresh the page.', response.status);
        }

        throw new ApiError(detail, response.status);
      }

      throw new ApiError(detail || `HTTP error ${response.status}`, response.status);
    }

    return response.json();
  }

  // Auth endpoints
  async getCurrentUser(): Promise<UserInfo> {
    return this.request<UserInfo>('/auth/me');
  }

  async getAdminCustomers(): Promise<AdminCustomer[]> {
    return this.request<AdminCustomer[]>('/admin/customers');
  }

  async getEmulationTargets(accountId: string): Promise<EmulationTarget[]> {
    return this.request<EmulationTarget[]>(`/admin/customers/${encodeURIComponent(accountId)}/emulation-targets`);
  }

  async getConsentInfo(): Promise<ConsentInfo> {
    return this.request<ConsentInfo>('/admin/consent-info');
  }

  async startEmulation(customerAccountId: string, userEmail: string): Promise<UserInfo> {
    return this.request<UserInfo>('/admin/emulation/start', {
      method: 'POST',
      body: JSON.stringify({
        customer_account_id: customerAccountId,
        user_email: userEmail,
      }),
    });
  }

  // Dashboard endpoints
  async getDashboardSummary(startDate?: string, endDate?: string, customerName?: string, branchName?: string, timezone?: string): Promise<DashboardSummary> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    if (customerName) params.append('customer_name', customerName);
    if (branchName) params.append('branch_name', branchName);
    const query = params.toString();
    return this.request<DashboardSummary>(`/dashboard/summary${query ? `?${query}` : ''}`);
  }

  async getCallVolume(startDate?: string, endDate?: string, customerName?: string, branchName?: string, timezone?: string): Promise<CallVolume> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    if (customerName) params.append('customer_name', customerName);
    if (branchName) params.append('branch_name', branchName);
    const query = params.toString();
    return this.request<CallVolume>(`/dashboard/call-volume${query ? `?${query}` : ''}`);
  }

  async getQualityMetrics(startDate?: string, endDate?: string, customerName?: string, branchName?: string, timezone?: string): Promise<QualityMetrics> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    if (customerName) params.append('customer_name', customerName);
    if (branchName) params.append('branch_name', branchName);
    const query = params.toString();
    return this.request<QualityMetrics>(`/dashboard/quality-metrics${query ? `?${query}` : ''}`);
  }

  async getBadCallStats(startDate?: string, endDate?: string, customerName?: string, branchName?: string, timezone?: string): Promise<BadCallStats> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    if (customerName) params.append('customer_name', customerName);
    if (branchName) params.append('branch_name', branchName);
    const query = params.toString();
    return this.request<BadCallStats>(`/dashboard/bad-calls${query ? `?${query}` : ''}`);
  }

  async getTrends(startDate?: string, endDate?: string, granularity?: string, customerName?: string, branchName?: string, timezone?: string): Promise<TrendData> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    if (granularity) params.append('granularity', granularity);
    if (customerName) params.append('customer_name', customerName);
    if (branchName) params.append('branch_name', branchName);
    const query = params.toString();
    return this.request<TrendData>(`/dashboard/trends${query ? `?${query}` : ''}`);
  }

  async getBranchQuality(startDate?: string, endDate?: string, customerName?: string, timezone?: string): Promise<BranchQualityResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (customerName) params.append('customer_name', customerName);
    if (timezone) params.append('timezone', timezone);
    const query = params.toString();
    return this.request<BranchQualityResponse>(`/dashboard/branches/quality${query ? `?${query}` : ''}`);
  }

  async getMySummary(startDate?: string, endDate?: string, timezone?: string): Promise<MySummary> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    const query = params.toString();
    return this.request<MySummary>(`/dashboard/my-summary${query ? `?${query}` : ''}`);
  }

  async getMyDashboard(startDate?: string, endDate?: string, timezone?: string, topContactsLimit = 6, recentCallsLimit = 8): Promise<MyDashboardResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    params.append('top_contacts_limit', String(topContactsLimit));
    params.append('recent_calls_limit', String(recentCallsLimit));
    const query = params.toString();
    return this.request<MyDashboardResponse>(`/dashboard/my-dashboard${query ? `?${query}` : ''}`);
  }

  async getMyTrends(startDate?: string, endDate?: string, timezone?: string): Promise<MyTrends> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    const query = params.toString();
    return this.request<MyTrends>(`/dashboard/my-trends${query ? `?${query}` : ''}`);
  }

  async getMyTopContacts(startDate?: string, endDate?: string, limit?: number, timezone?: string): Promise<MyTopContactsResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    if (limit) params.append('limit', String(limit));
    const query = params.toString();
    return this.request<MyTopContactsResponse>(`/dashboard/my-top-contacts${query ? `?${query}` : ''}`);
  }

  async getMyRecentCalls(startDate?: string, endDate?: string, limit?: number, timezone?: string): Promise<MyRecentCallsResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (timezone) params.append('timezone', timezone);
    if (limit) params.append('limit', String(limit));
    const query = params.toString();
    return this.request<MyRecentCallsResponse>(`/dashboard/my-recent-calls${query ? `?${query}` : ''}`);
  }

  // Calls endpoints
  async getCustomers(): Promise<string[]> {
    return this.request<string[]>('/calls/customers');
  }

  async getBranches(): Promise<{ name: string; customer_name?: string }[]> {
    return this.request<{ name: string }[]>('/calls/branches');
  }

  async getCalls(filters: CallFilters = {}, timezone?: string, signal?: AbortSignal): Promise<CallListResponse> {
    const params = new URLSearchParams();
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.quality) params.append('quality', filters.quality);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (timezone) params.append('timezone', timezone);
    if (filters.search) params.append('search', filters.search);
    if (filters.branch_id) params.append('branch_id', String(filters.branch_id));
    if (filters.branch_name) params.append('branch_name', filters.branch_name);
    if (filters.customer_name) params.append('customer_name', filters.customer_name);
    if (filters.voice_app_only) params.append('voice_app_only', 'true');
    if (filters.page) params.append('page', String(filters.page));
    if (filters.page_size) params.append('page_size', String(filters.page_size));
    const query = params.toString();
    return this.request<CallListResponse>(
      `/calls${query ? `?${query}` : ''}`,
      { signal },
    );
  }

  async getCallDetail(sessionId: string): Promise<CallDetail> {
    return this.request<CallDetail>(`/calls/${encodeURIComponent(sessionId)}`);
  }

  async getCallLegs(multiSessionId: string): Promise<CallLegsResponse> {
    return this.request<CallLegsResponse>(`/calls/legs/${encodeURIComponent(multiSessionId)}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getCallLegsDebug(multiSessionId: string): Promise<any> {
    return this.request(`/calls/legs/${encodeURIComponent(multiSessionId)}/debug`);
  }

  // Recordings
  async getRecordings(params: {
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<RecordingListResponse> {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    if (params.page) q.append('page', String(params.page));
    if (params.page_size) q.append('page_size', String(params.page_size));
    const query = q.toString();
    return this.request<RecordingListResponse>(`/recordings${query ? `?${query}` : ''}`);
  }

  async getRecordingDownloadUrl(recordingId: number): Promise<RecordingDownloadResponse> {
    return this.request<RecordingDownloadResponse>(`/recordings/${recordingId}/download`);
  }

  // Company (Taiga) dataset
  async getCompanyTaigaDataset(days = 90): Promise<CompanyDatasetResponse> {
    return this.request<CompanyDatasetResponse>(`/company/taiga-dataset?days=${days}`);
  }

  async getCompanyTaigaHeatmap(days = 30): Promise<CompanyHeatmapResponse> {
    return this.request<CompanyHeatmapResponse>(`/company/taiga-heatmap?days=${days}`);
  }

  // Queues (VAAC) dataset
  //
  // `timezone` anchors the server's trailing-N-day window on the viewer's
  // local "today" instead of the server process's own clock (Migration
  // Slice 3 of the application-wide date/timezone contract) -- always pass
  // the screen's selected IANA zone from useTimezone().
  async getQueuesTaigaDataset(
    days = 30,
    timezone: string,
    scope?: BranchScope,
  ): Promise<QueuesDatasetResponse> {
    return this.request<QueuesDatasetResponse>(
      `/queues/taiga-dataset?days=${days}&timezone=${encodeURIComponent(timezone)}${branchScopeQs(scope)}`,
    );
  }

  async getQueuesTaigaTrends(
    days = 30,
    timezone: string,
    scope?: BranchScope,
  ): Promise<TrendsResponse> {
    return this.request<TrendsResponse>(
      `/queues/taiga-trends?days=${days}&timezone=${encodeURIComponent(timezone)}${branchScopeQs(scope)}`,
    );
  }

  async getQueuesTaigaServiceLevel(
    days = 30,
    timezone: string,
    primary = 20,
    secondary = 30,
    scope?: BranchScope,
  ): Promise<ServiceLevelResponse> {
    return this.request<ServiceLevelResponse>(
      `/queues/taiga-service-level?days=${days}&timezone=${encodeURIComponent(timezone)}&threshold_primary=${primary}&threshold_secondary=${secondary}${branchScopeQs(scope)}`,
    );
  }

  async getQueuesTaigaAADetail(
    days = 30,
    timezone: string,
    scope?: BranchScope,
  ): Promise<AADetailResponse> {
    return this.request<AADetailResponse>(
      `/queues/taiga-aa-detail?days=${days}&timezone=${encodeURIComponent(timezone)}${branchScopeQs(scope)}`,
    );
  }

  async getQueuesTaigaAgents(
    days = 30,
    timezone: string,
    scope?: BranchScope,
  ): Promise<AgentDetailResponse> {
    return this.request<AgentDetailResponse>(
      `/queues/taiga-agents?days=${days}&timezone=${encodeURIComponent(timezone)}${branchScopeQs(scope)}`,
    );
  }

  // Branch analytics
  async getBranchAnalytics(branchId: number, days = 30): Promise<BranchAnalyticsResponse> {
    return this.request<BranchAnalyticsResponse>(`/branch/${branchId}/analytics?days=${days}`);
  }

  async getAccessibleBranches(): Promise<BranchMetaResponse[]> {
    return this.request<BranchMetaResponse[]>(`/branch/accessible`);
  }

  // Support reports ("Report a problem", spec §7.4)
  async createSupportReport(body: {
    interaction_id: string;
    category: ReportCategory;
    description: string;
    override?: boolean;
  }): Promise<SupportReportSummary> {
    return this.request<SupportReportSummary>('/support-reports', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getSupportReportForInteraction(interactionId: string): Promise<SupportReportSummary | null> {
    return this.request<SupportReportSummary | null>(
      `/support-reports?interaction_id=${encodeURIComponent(interactionId)}`
    );
  }

  async createMissingCallReport(body: {
    idempotency_key: string;
    description: string;
    approximate_time?: string;
    timezone?: string;
    direction: 'inbound' | 'outbound' | 'unknown';
    phone_number?: string;
    expected_user?: string;
    expected_queue?: string;
    expected_branch?: string;
  }): Promise<SupportReportSummary> {
    return this.request<SupportReportSummary>('/support-reports/missing-call', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Tenant support destination setting (spec §10)
  async getSupportDestination(): Promise<SupportDestination> {
    return this.request<SupportDestination>('/settings/support-destination');
  }

  async setSupportDestination(address: string | null): Promise<SupportDestination> {
    return this.request<SupportDestination>('/settings/support-destination', {
      method: 'PUT',
      body: JSON.stringify({ support_email_ingest_address: address }),
    });
  }

  async testSupportDestination(): Promise<{ sent: boolean; error: string | null }> {
    return this.request<{ sent: boolean; error: string | null }>('/settings/support-destination/test', {
      method: 'POST',
    });
  }
}

export const api = new ApiService();
